import './styles.css';
import logoUrl from './assets/gamercubic-logo.webp';
import {
  MAP_SPANS,
  PORTAL_RULES,
  convertOverworldToNether,
  formatCoordinate,
  isPortalWithinSearchRange,
  mapPointToWorld,
  parseCoordinate,
  searchRadiusFor,
  synchronizeExistingPortal,
} from './calculator';
import { describePortalStatus, renderPortalMap } from './map';
import { CALCULATOR_TEMPLATE } from './ui/template';
import type { Axis, CoordinateStrings, Coordinates, Dimension, Edition, ExistingPortal } from './types';

const DEFAULT_OVERWORLD: Coordinates = { x: 344, y: 64, z: -1290 };

interface CalculatorState {
  edition: Edition;
  drafts: Record<Dimension, CoordinateStrings>;
  values: Record<Dimension, Coordinates>;
  existingPortal: ExistingPortal | null;
}

function toDrafts(coords: Coordinates): CoordinateStrings {
  return {
    x: formatCoordinate(coords.x),
    y: formatCoordinate(coords.y),
    z: formatCoordinate(coords.z),
  };
}

function makeInitialState(): CalculatorState {
  const nether = convertOverworldToNether(DEFAULT_OVERWORLD);
  return {
    edition: 'java',
    drafts: { overworld: toDrafts(DEFAULT_OVERWORLD), nether: toDrafts(nether) },
    values: { overworld: { ...DEFAULT_OVERWORLD }, nether },
    existingPortal: null,
  };
}

const appMount = document.querySelector<HTMLElement>('#gc-nether-portal-app');

if (!appMount) {
  throw new Error('Minecraft Nether Portal Calculator mount was not found.');
}

const mount: HTMLElement = appMount;
mount.innerHTML = CALCULATOR_TEMPLATE;
document.querySelectorAll<HTMLImageElement>('[data-gc-npc-logo]').forEach((image) => { image.src = logoUrl; });

let state = makeInitialState();
let copyMessageTimer = 0;

const inputElements = Array.from(mount.querySelectorAll<HTMLInputElement>('input[data-dimension][data-axis]'));
const editionButtons = Array.from(mount.querySelectorAll<HTMLButtonElement>('[data-edition]'));
const maps: Record<Dimension, HTMLCanvasElement> = {
  overworld: mount.querySelector<HTMLCanvasElement>('#gc-npc-overworld-map')!,
  nether: mount.querySelector<HTMLCanvasElement>('#gc-npc-nether-map')!,
};

function inputFor(dimension: Dimension, axis: Axis): HTMLInputElement {
  return mount.querySelector<HTMLInputElement>(`input[data-dimension="${dimension}"][data-axis="${axis}"]`)!;
}

function markInputValidity(input: HTMLInputElement, rawValue: string): void {
  const incomplete = rawValue.trim() === '' || rawValue.trim() === '-';
  const valid = parseCoordinate(rawValue) !== null;
  input.classList.toggle('is-invalid', !incomplete && !valid);
  if (!incomplete && !valid) input.setAttribute('aria-invalid', 'true');
  else input.removeAttribute('aria-invalid');
}

function updateCounterpart(source: Dimension, axis: Axis, value: number): void {
  const target: Dimension = source === 'overworld' ? 'nether' : 'overworld';
  const converted = axis === 'y'
    ? value
    : source === 'overworld'
      ? Math.floor(value / 8)
      : value * 8;
  state.values[source][axis] = value;
  state.values[target][axis] = converted;
  state.drafts[target][axis] = formatCoordinate(converted);
}

function renderFields(): void {
  inputElements.forEach((input) => {
    const dimension = input.dataset.dimension as Dimension;
    const axis = input.dataset.axis as Axis;
    if (document.activeElement !== input) input.value = state.drafts[dimension][axis];
    markInputValidity(input, state.drafts[dimension][axis]);
  });
}

function renderEdition(): void {
  editionButtons.forEach((button) => {
    const isActive = button.dataset.edition === state.edition;
    button.setAttribute('aria-pressed', String(isActive));
  });
  const rule = PORTAL_RULES[state.edition];
  mount.querySelector<HTMLElement>('#gc-npc-rule-note')!.textContent = state.edition === 'java'
    ? `Java searches ${rule.overworldSearchRadius} blocks in the Overworld and ${rule.netherSearchRadius} blocks in the Nether.`
    : `Bedrock uses a ${rule.overworldSearchRadius}-block horizontal search range in both dimensions.`;
  const y = state.values.nether.y;
  const heightNote = mount.querySelector<HTMLElement>('#gc-npc-height-note')!;
  if (y < 0) {
    heightNote.textContent = `Nether Y ${formatCoordinate(y)} is below the normal buildable range. Use Y 0 or higher.`;
  } else if (state.edition === 'bedrock' && y > 127) {
    heightNote.textContent = `Nether Y ${formatCoordinate(y)} is above Bedrock's build limit. Move the full portal frame to Y 127 or lower.`;
  } else if (state.edition === 'java' && y >= 256) {
    heightNote.textContent = `Nether Y ${formatCoordinate(y)} is outside Java's build height. Move the full portal frame below Y 256.`;
  } else if (state.edition === 'java' && y > 127) {
    heightNote.textContent = `Nether Y ${formatCoordinate(y)} is above the bedrock roof. Portals do not generate there; manual roof building only works where world and server rules allow it.`;
  } else {
    heightNote.textContent = rule.heightNote;
  }
}

function renderCommands(): void {
  (['overworld', 'nether'] as Dimension[]).forEach((dimension) => {
    const rules = PORTAL_RULES[state.edition];
    const dimensionId = dimension === 'overworld' ? rules.overworldDimensionId : rules.netherDimensionId;
    const xyz = (['x', 'y', 'z'] as Axis[]).map((axis) => {
      const parsed = parseCoordinate(state.drafts[dimension][axis]);
      return parsed === null ? '~' : formatCoordinate(parsed);
    }).join(' ');
    mount.querySelector<HTMLElement>(`#gc-npc-${dimension}-command`)!.textContent = `/execute in ${dimensionId} run tp @s ${xyz}`;
  });
}

function formattedDraftCoordinates(dimension: Dimension): string {
  return (['x', 'y', 'z'] as Axis[]).map((axis) => {
    const parsed = parseCoordinate(state.drafts[dimension][axis]);
    return parsed === null ? '~' : formatCoordinate(parsed);
  }).join(' ');
}

function renderMaps(): void {
  (['overworld', 'nether'] as Dimension[]).forEach((dimension) => {
    const coords = state.values[dimension];
    const center = { x: coords.x, z: coords.z };
    renderPortalMap({
      canvas: maps[dimension],
      dimension,
      edition: state.edition,
      center,
      existingPortal: state.existingPortal,
    });
    const name = dimension === 'overworld' ? 'Overworld' : 'Nether';
    const centerText = `Target X ${formatCoordinate(coords.x)}, Z ${formatCoordinate(coords.z)}`;
    mount.querySelector<HTMLElement>(`#gc-npc-${dimension}-center`)!.textContent = centerText;
    const summary = describePortalStatus(dimension, state.edition, center, state.existingPortal);
    mount.querySelector<HTMLElement>(`#gc-npc-${dimension}-summary`)!.textContent = summary;
    maps[dimension].setAttribute('aria-label', `${name} portal map. ${centerText}. ${summary} X increases right and Z increases down. Press Enter to place a portal at the center.`);
  });

  const clearButton = mount.querySelector<HTMLButtonElement>('#gc-npc-clear-portal')!;
  clearButton.disabled = !state.existingPortal;
  const capture = mount.querySelector<HTMLElement>('#gc-npc-capture')!;
  if (!state.existingPortal) {
    capture.className = 'gc-npc-capture';
    capture.innerHTML = '<strong>No existing portal placed</strong><span>Click either map to check whether another portal could capture the link.</span>';
    return;
  }

  const inOverworldRange = isPortalWithinSearchRange(
    { x: state.values.overworld.x, z: state.values.overworld.z },
    state.existingPortal.overworld,
    searchRadiusFor(state.edition, 'overworld'),
  );
  const inNetherRange = isPortalWithinSearchRange(
    { x: state.values.nether.x, z: state.values.nether.z },
    state.existingPortal.nether,
    searchRadiusFor(state.edition, 'nether'),
  );
  const couldCapture = inOverworldRange || inNetherRange;
  capture.className = `gc-npc-capture ${couldCapture ? 'is-warning' : 'is-safe'}`;
  capture.innerHTML = couldCapture
    ? `<strong>Potential portal capture</strong><span>The marker is inside ${inOverworldRange && inNetherRange ? 'both search areas' : `the ${inOverworldRange ? 'Overworld' : 'Nether'} search area`}. Check Y distance and nearby portals in-game before travelling.</span>`
    : '<strong>Outside the shown search areas</strong><span>This portal is outside both horizontal X/Z search areas for the current target. Vertical distance and other nearby portals can still affect the in-game result.</span>';
}

function renderAll(): void {
  renderFields();
  renderEdition();
  renderCommands();
  renderMaps();
}

function showCopyMessage(message: string): void {
  const status = mount.querySelector<HTMLElement>('#gc-npc-copy-status')!;
  window.clearTimeout(copyMessageTimer);
  status.textContent = message;
  copyMessageTimer = window.setTimeout(() => { status.textContent = ''; }, 1800);
}

async function copyText(text: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (!copied) throw new Error('Clipboard copy failed');
  }
  showCopyMessage(`${label} copied.`);
}

function placePortal(dimension: Dimension, clientX?: number, clientY?: number): void {
  const canvas = maps[dimension];
  const rect = canvas.getBoundingClientRect();
  const point = clientX === undefined || clientY === undefined
    ? { x: state.values[dimension].x, z: state.values[dimension].z }
    : mapPointToWorld(
      Math.min(rect.width, Math.max(0, clientX - rect.left)),
      Math.min(rect.height, Math.max(0, clientY - rect.top)),
      rect.width,
      rect.height,
      { x: state.values[dimension].x, z: state.values[dimension].z },
      MAP_SPANS[dimension],
    );
  state.existingPortal = synchronizeExistingPortal(dimension, point);
  renderMaps();
}

inputElements.forEach((input) => {
  input.addEventListener('input', () => {
    const dimension = input.dataset.dimension as Dimension;
    const axis = input.dataset.axis as Axis;
    state.drafts[dimension][axis] = input.value;
    markInputValidity(input, input.value);
    const parsed = parseCoordinate(input.value);
    if (parsed === null) {
      const opposite: Dimension = dimension === 'overworld' ? 'nether' : 'overworld';
      state.drafts[opposite][axis] = '';
      const oppositeInput = inputFor(opposite, axis);
      oppositeInput.value = '';
      markInputValidity(oppositeInput, '');
      renderCommands();
      return;
    }
    updateCounterpart(dimension, axis, parsed);
    const opposite: Dimension = dimension === 'overworld' ? 'nether' : 'overworld';
    const oppositeInput = inputFor(opposite, axis);
    oppositeInput.value = state.drafts[opposite][axis];
    markInputValidity(oppositeInput, oppositeInput.value);
    renderEdition();
    renderCommands();
    renderMaps();
  });
  input.addEventListener('blur', () => {
    const dimension = input.dataset.dimension as Dimension;
    const axis = input.dataset.axis as Axis;
    const parsed = parseCoordinate(input.value);
    if (parsed === null) {
      const restored = state.values[dimension][axis];
      state.drafts[dimension][axis] = formatCoordinate(restored);
      updateCounterpart(dimension, axis, restored);
      input.value = state.drafts[dimension][axis];
      markInputValidity(input, input.value);
      renderAll();
    }
  });
});

editionButtons.forEach((button) => {
  button.addEventListener('click', () => {
    state.edition = button.dataset.edition as Edition;
    renderEdition();
    renderCommands();
    renderMaps();
  });
});

mount.querySelector<HTMLButtonElement>('#gc-npc-reset')!.addEventListener('click', () => {
  state = makeInitialState();
  renderAll();
  showCopyMessage('Calculator reset.');
});

mount.querySelectorAll<HTMLButtonElement>('[data-copy-coords]').forEach((button) => {
  button.addEventListener('click', () => {
    const dimension = button.dataset.copyCoords as Dimension;
    void copyText(formattedDraftCoordinates(dimension), `${dimension === 'overworld' ? 'Overworld' : 'Nether'} coordinates`);
  });
});

mount.querySelectorAll<HTMLButtonElement>('[data-copy-command]').forEach((button) => {
  button.addEventListener('click', () => {
    const dimension = button.dataset.copyCommand as Dimension;
    const command = mount.querySelector<HTMLElement>(`#gc-npc-${dimension}-command`)!.textContent || '';
    void copyText(command, 'Teleport command');
  });
});

mount.querySelector<HTMLButtonElement>('#gc-npc-clear-portal')!.addEventListener('click', () => {
  state.existingPortal = null;
  renderMaps();
});

(Object.entries(maps) as [Dimension, HTMLCanvasElement][]).forEach(([dimension, canvas]) => {
  canvas.addEventListener('click', (event) => placePortal(dimension, event.clientX, event.clientY));
  canvas.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      placePortal(dimension);
    }
  });
});

const observer = new ResizeObserver(() => renderMaps());
Object.values(maps).forEach((canvas) => observer.observe(canvas));
window.addEventListener('resize', renderMaps);

renderAll();
